import React, { useState, useEffect, useCallback } from 'react';
import { Table, DatePicker, Button, Select, message, Alert, Spin } from 'antd';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { profitAndLoss, getCustomerQb } from '~/services/hooks/quickbooksApi';
import './style.css';
import { useTranslation } from 'react-i18next';
import { parseProfitAndLossData } from '~/Utils/parseReportData';

const { RangePicker } = DatePicker;

export default function ProfitAndLossReport({ name, qbId }) {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState([moment().startOf('year'), moment().endOf('year')]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [noData, setNoData] = useState(false);

  // limpa tudo para nova pesquisa
  const resetReport = () => {
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setDataSource([]);
    setReportLoaded(false);
    setNoData(false);
  };

  const hasRows = arr => Array.isArray(arr) && arr.length > 0;

  // fetchData agora aceita customerId override
  const fetchData = async (startDate, endDate, customerIdOverride) => {
    const customerId = customerIdOverride ?? selectedCustomer?.id;
    if (!customerId) return;

    setLoading(true);
    setReportLoaded(false);
    setNoData(false);

    try {
      const reportData = await profitAndLoss({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        customerId,
      });

      if (hasRows(reportData?.Rows?.Row)) {
        setDataSource(parseProfitAndLossData(reportData));
        setReportLoaded(true);
      } else {
        setDataSource([]);
        setNoData(true);
      }
    } catch (err) {
      console.error(err);
      message.error(t('screens:reports.generateError'));
    } finally {
      setLoading(false);
    }
  };

  // busca clientes e dispara relatório com o primeiro encontrado
  const fetchCustomerAndReport = async customerName => {
    if (!customerName) return resetReport();

    try {
      const resp = await getCustomerQb({ name });
      const customers = resp?.QueryResponse?.Customer ?? [];
      const options = customers.map(c => ({
        label: c.FullyQualifiedName,
        value: c.Id,
      }));
      setCustomerOptions(options);

      if (options.length > 0) {
        const first = options[0];
        setSelectedCustomer({ id: first.value, name: first.label });
        // dispara relatório imediatamente com a primeira opção
        await fetchData(dateRange[0], dateRange[1], first.value);
      } else {
        resetReport();
        message.info(t('screens:reports.noCustomerFound'));
      }
    } catch (err) {
      console.error(err);
      message.error(t('screens:reports.customerSearchError'));
    }
  };

  useEffect(() => {
    if (qbId) {
      setSelectedCustomer({ id: qbId, name });
      fetchData(dateRange[0], dateRange[1], qbId);
    } else {
      fetchCustomerAndReport(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, qbId]);

  // para busca incremental via input, se ainda quiser manter
  const handleCustomerSearch = async value => {
    if (!value) return setCustomerOptions([]);
    try {
      const resp = await getCustomerQb({ name });
      const customers = resp?.QueryResponse?.Customer ?? [];
      setCustomerOptions(
        customers.map(c => ({
          label: c.FullyQualifiedName,
          value: c.Id,
        }))
      );
    } catch (err) {
      console.error(err);
      message.error(t('screens:reports.customerSearchError'));
    }
  };
  const debouncedSearch = useCallback(debounce(handleCustomerSearch, 500), []);

  const columns = [
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      render: (text, record) => <span style={{ paddingLeft: record.level * 20 }}>{text}</span>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: text =>
        text === '' ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(text)),
    },
  ];

  return (
    <Spin
      spinning={loading}
      tip={t('screens:reports.loading')} // ou "Carregando relatório..."
      size="large"
      style={{ width: '100%' }}
    >
      <RangePicker
        style={{ marginBottom: 16 }}
        value={dateRange}
        onChange={dates => setDateRange(dates)}
        format="YYYY-MM-DD"
      />

      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Select
          showSearch
          placeholder={t('screens:reports.customerSearchPlaceholder')}
          style={{ width: 600, display: qbId ? 'none' : 'inline-block' }}
          filterOption={false}
          onSearch={debouncedSearch}
          onChange={(value, option) => {
            setSelectedCustomer({ id: value, name: option.label });
            fetchData(dateRange[0], dateRange[1], value);
          }}
          notFoundContent={null}
          options={customerOptions}
          value={selectedCustomer?.id}
        />
        {/* {selectedCustomer && (
            <Button icon={<CloseOutlined />} onClick={resetReport} />
          )} */}
      </div>

      <Button
        style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}
        type="primary"
        onClick={() => fetchData(dateRange[0], dateRange[1])}
        loading={loading}
      >
        {t('screens:reports.generateReport')}
      </Button>

      {reportLoaded && selectedCustomer?.name && <h2 style={{ padding: '20px 0' }}>{selectedCustomer.name}</h2>}

      {noData && (
        <Alert
          message={t('screens:reports.noData')}
          description={t('screens:reports.noDataDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered
        rowClassName={record => (record.isSummary ? 'summary-row' : '')}
      />
    </Spin>
  );
}
